class TradeRequestForm extends window.BAO.CustomElement() {
  static get requiredAttributes () {
    return [
      'data-klaviyo-key',
      'data-thank-you-page',
      'data-error-required-field',
      'data-error-email',
      'data-error-phone',
      'data-error-general',
    ]
  }

  // Fields that map to Klaviyo's reserved top-level profile attributes.
  static attributeFields = {
    email: '#TradeFormEmail',
    phone_number: '#TradeFormPhone',
    first_name: '#TradeFormFirstName',
    last_name: '#TradeFormLastName',
  }

  // Key = Klaviyo property name, value = the input/select/textarea selector.
  static propertyFields = {
    BusinessName: '#TradeFormBusinessName',
    BusinessRole: '#TradeFormBusinessRole',
    Email: '#TradeFormEmail',
    PhoneNumber: '#TradeFormPhone',
    VAT_EORI: '#TradeFormEORI',
    BusinessRegistrationNumber: '#TradeFormBusinessRegNumber',
    BusinessAddressLine1: '#TradeFormAddress',
    BusinessAddressLine2: '#TradeFormAddress2',
    BusinessAddressCity: '#TradeFormCity',
    BusinessAddressPostcode: '#TradeFormPostcode',
    BusinessAddressCountry: '#TradeFormCountry',
    BusinessLinks: '#TradeFormLinks',
  }

  connectedCallback () {
    super.connectedCallback()

    // Scope lookups to this component rather than the whole document,
    // so two instances on one page can't grab each other's elements.
    this.form = this.querySelector('#TradeSignupForm')
    this.messageEl = this.querySelector('#TradeSignupFormMessage')
    this.submitBtn = this.querySelector('#TradeSignupFormSubmit')

    if (!this.form || !this.messageEl || !this.submitBtn) {
      console.error('TradeRequestForm: missing form, message, or submit element')
      return
    }

    this.onSubmit = event => this.handleSubmit(event)
    this.form.addEventListener('submit', this.onSubmit)
  }

  disconnectedCallback () {
    if (this.form && this.onSubmit) {
      this.form.removeEventListener('submit', this.onSubmit)
    }
    super.disconnectedCallback?.()
  }

  async handleSubmit (event) {
    event.preventDefault()
    this.messageEl.hidden = true

    if (!this.validate()) {
      this.showMessage(this.getAttribute('data-error-general'), true)
      return
    }

    this.submitBtn.disabled = true

    const endpoint = `https://a.klaviyo.com/client/profiles/?company_id=${encodeURIComponent(this.getAttribute('data-klaviyo-key'))}`

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: '2024-10-15',
        },
        body: JSON.stringify(this.buildPayload()),
      })

      if (response.ok) {
        this.trackTradeApplicationReceived()

        window.location.href = this.getAttribute('data-thank-you-page')
        return
      }

      const errorBody = await response.json().catch(() => null)
      console.error('Klaviyo submission error:', errorBody)
      this.showMessage(this.getAttribute('data-error-general'), true)
      this.submitBtn.disabled = false
    } catch (err) {
      console.error('Klaviyo submission error:', err)
      this.showMessage(this.getAttribute('data-error-general'), true)
      this.submitBtn.disabled = false
    }
  }

  // Fire-and-forget: tracking shouldn't block the redirect to the thank you page.
  trackTradeApplicationReceived () {
    const endpoint = `https://a.klaviyo.com/client/events/?company_id=${encodeURIComponent(this.getAttribute('data-klaviyo-key'))}`
    const email = this.fieldValue(TradeRequestForm.attributeFields.email)

    const payload = {
      data: {
        type: 'event',
        attributes: {
          properties: {
            email,
            store: Shopify.shop,
            $event_id: Math.floor(Math.random() * 2000) + 1,
          },
          time: Date.now() / 1000,
          metric: {
            data: {
              type: 'metric',
              attributes: { name: 'Trade Application Received' },
            },
          },
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email,
              },
            },
          },
        },
      },
    }

    // keepalive lets the request complete after the redirect below unloads the page.
    fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: '2024-10-15',
      },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(err => console.error('Klaviyo event tracking error:', err))
  }

  validate () {
    this.clearFieldErrors()
    let valid = true

    this.form.querySelectorAll('[required]').forEach(field => {
      const errorSpan = this.errorSpanFor(field)
      const value = field.value ? field.value.trim() : ''

      if (!value) {
        valid = false
        this.markFieldInvalid(field, errorSpan, this.getAttribute('data-error-required-field'))
        return
      }

      if (field.id === 'TradeFormEmail' && !this.isValidEmail(value)) {
        valid = false
        this.markFieldInvalid(field, errorSpan, this.getAttribute('data-error-email'))
      }

      if (field.id === 'TradeFormPhone' && !this.isValidPhone(value)) {
        valid = false
        this.markFieldInvalid(field, errorSpan, this.getAttribute('data-error-phone'))
      }
    })

    return valid
  }

  errorSpanFor (field) {
    const sibling = field.nextElementSibling
    return sibling && sibling.classList.contains('frm-Form_FieldError') ? sibling : null
  }

  markFieldInvalid (field, errorSpan, message) {
    field.classList.add('frm-Form_Input-error')
    if (errorSpan) {
      errorSpan.hidden = false
      errorSpan.textContent = message
    }
  }

  clearFieldErrors () {
    this.form.querySelectorAll('.frm-Form_FieldError').forEach(el => {
      el.hidden = true
      el.textContent = ''
    })
    this.form.querySelectorAll('.frm-Form_Input-error').forEach(el => {
      el.classList.remove('frm-Form_Input-error')
    })
  }

  isValidEmail (value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
  }

  isValidPhone (value) {
    const digits = value.replace(/\D/g, '')
    return digits.length >= 10
  }

  fieldValue (selector) {
    const el = this.form.querySelector(selector)
    return el ? el.value.trim() : ''
  }

  showMessage (text, isError) {
    this.messageEl.textContent = text
    this.messageEl.hidden = false
    this.messageEl.classList.toggle('val-Validation-error', isError)
    this.messageEl.classList.toggle('val-Validation-success', !isError)
  }

  buildPayload () {
    const propertyPairs = Object.entries(TradeRequestForm.propertyFields)
      .map(([key, selector]) => [key, this.fieldValue(selector)])
      .filter(([, value]) => value)
      .map(([key, value]) => `${key}: ${value}`)

    const attributes = {
      properties: {
        tradeRequestData: propertyPairs.join(', '),
        TradeRequested: true,
      },
    }

    Object.entries(TradeRequestForm.attributeFields).forEach(([key, selector]) => {
      const value = this.fieldValue(selector)
      if (value) attributes[key] = value
    })

    return {
      data: {
        type: 'profile',
        attributes,
      },
    }
  }
}

if (!customElements.get('trade-request-form')) {
  customElements.define('trade-request-form', TradeRequestForm)
}
